import React, { useState } from "react";
import styles from "~/pages/admin/jobs/add-job/AddJobModal.module.scss";
import classNames from "classnames/bind";
import { useLoadScript } from "@react-google-maps/api";
import { googlePlacesApiKey } from "~/utils/constants/env";

// Components
import AddJobModalHeader from "~/pages/admin/jobs/add-job/components/AddJobModalHeader";
import AddJobModalActions from "~/pages/admin/jobs/add-job/components/AddJobModalActions";
import { JobAddressInput } from "~/pages/admin/jobs/add-job/components/AddressStep";
import {
  JobBasicInfo,
  JobDetailsForm,
  JobTextAreas,
} from "~/pages/admin/jobs/add-job/components/JobDetailsStep";

// Hooks
import { useJobForm } from "~/pages/admin/jobs/add-job/hooks/useJobForm";
import { useJobData } from "~/pages/admin/jobs/add-job/hooks/useJobData";

// Types
import {
  AddressSuggestion,
  ModalStep,
} from "~/pages/admin/jobs/add-job/types/add-job.types";

// Constants
import { JobStageOptions } from "~/pages/admin/jobs/add-job/constants/add-job.constants";
import BaseModal from "~/components/common/base-modal/BaseModal";

const cx = classNames.bind(styles);

interface AddJobFromClientModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (data: any) => void;
  clientId: string;
  clientAddress: string;
}

const AddJobFromClientModal: React.FC<AddJobFromClientModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  clientId,
  clientAddress,
}) => {
  // Custom Hooks
  const {
    formData,
    errors,
    updateField,
    updateError,
    validateDetailsStep,
    isDetailsStepValid,
  } = useJobForm();

  const { salesReps, tradeTypes } = useJobData(isOpen);

  // Local State - Start from step 2
  const [modalStep, setModalStep] = useState<ModalStep>(2);
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<AddressSuggestion | null>(null);
  const [customerAddress] = useState<string>(clientAddress);
  const [useCustomerAddress, setUseCustomerAddress] = useState<boolean>(false);

  // Google Maps
  const { isLoaded } = useLoadScript({
    googleMapsApiKey:
      googlePlacesApiKey || "AIzaSyDGQ-_dJ3HEO9pWMf_LzWO8wxH2Vce2i58",
  });

  const handleBackStep = () => {
    if (modalStep === 3) {
      setModalStep(2);
    }
  };

  const handleAddressSuggestionSelect = (suggestion: AddressSuggestion) => {
    setSelectedSuggestion(suggestion);
    const newAddress = suggestion?.placePrediction?.text?.text || "";
    updateField("address", newAddress);
    updateField("latitude", 0);
    updateField("longitude", 0);
  };

  const handleConfirmAddress = async () => {
    const generateJobNumber = () => {
      const year = new Date().getFullYear();
      const randomNum = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, "0");
      return `JOB-${year}-${randomNum}`;
    };

    const goToJobDetailsStep = () => {
      updateField("jobNumber", generateJobNumber());
      setModalStep(3);
    };

    // If using customer address (no suggestion selected)
    if (useCustomerAddress && customerAddress) {
      updateField("address", customerAddress);
      updateField("latitude", 0);
      updateField("longitude", 0);
      goToJobDetailsStep();
      return;
    }

    // If using Google Places suggestion
    if (selectedSuggestion) {
      const placeId = selectedSuggestion?.placePrediction?.placeId;
      try {
        const res = await fetch(
          `https://places.googleapis.com/v1/places/${placeId}`,
          {
            method: "GET",
            headers: {
              "X-Goog-Api-Key":
                googlePlacesApiKey || "AIzaSyBFvJr6l0XfUv2axQAvHaHnTzwzwlKJ1oo",
              "X-Goog-FieldMask": "location,displayName,formattedAddress",
            },
          }
        );
        const data = await res.json();
        const location = data?.location;
        if (location) {
          updateField("latitude", location.latitude);
          updateField("longitude", location.longitude);
          updateField(
            "address",
            selectedSuggestion.placePrediction?.text?.text || ""
          );
        } else {
          updateField(
            "address",
            selectedSuggestion.placePrediction?.text?.text || ""
          );
        }
      } catch (err) {
        updateField(
          "address",
          selectedSuggestion.placePrediction?.text?.text || ""
        );
      }
      goToJobDetailsStep();
      return;
    }

    goToJobDetailsStep();
  };

  const handleCreateJob = () => {
    if (validateDetailsStep()) {
      // Transform formData to API format
      const transformData = (data: typeof formData) => {
        let jobStageValue = "LEAD";
        if (data.currentStage) {
          const stageOption = JobStageOptions.find(
            (opt) =>
              opt.label === data.currentStage || opt.value === data.currentStage
          );
          jobStageValue = stageOption?.value || data.currentStage || "LEAD";
        }

        const transformed: any = {};

        // Step 1: Use existing customer
        transformed.flagExistenceCustomer = true;
        transformed.primaryCustomerId = parseInt(clientId);

        // Step 2: flagUseCustomerAddress
        transformed.flagUseCustomerAddress = useCustomerAddress;

        if (!useCustomerAddress && data.address) {
          transformed.jobAddress = data.address;
          transformed.jobAddressLatitude = data.latitude || 0;
          transformed.jobAddressLongitude = data.longitude || 0;
        }

        transformed.flagAddNewBillingAddress = false;
        transformed.flagSameAsJobAddress = false;
        transformed.flagSameAsCustomerAddress = Boolean(customerAddress.trim());

        // Step 3: Standard fields
        transformed.jobNumber = data.jobNumber || "";
        transformed.jobTitle =
          data.jobName || data.jobDescription.trim() || "New Job";
        transformed.jobType = data.jobType || "";
        transformed.priority = data.priority || "MEDIUM";
        transformed.salesRepId = data.salesRepId ? parseInt(data.salesRepId) : 0;
        transformed.leadSource = data.leadSource || "";
        transformed.referralName = data.referralName || "";

        if (data.tradeType.length > 0) {
          transformed.tradeTypes = data.tradeType.map((id) => parseInt(id, 10));
        }

        transformed.color =
          data.shingle === "CUSTOM"
            ? (data.customShingleColor || "").trim()
            : data.shingle || "";
        transformed.shingleManufacturer = data.shinglesManufacturer || "";
        transformed.numberOfSquares = data.numberOfSquares || undefined;
        transformed.jobStage = jobStageValue;
        transformed.pitch = data.pitch || "";
        transformed.description = data.jobDescription || "";
        transformed.jobNote = data.notes || "";

        if (data.jobType === "Insurance") {
          transformed.insuranceCompanyName = data.insuranceCompanyName || "";
          transformed.insuranceEmail = data.insuranceEmail || "";
          transformed.insuranceClaimNumber = data.claimNumber || "";
          transformed.insuranceAdjusterName = data.adjusterName || "";
          transformed.insurancePhone = data.insurancePhoneNumber || "";
          transformed.insurancePolicyType = data.policyType || "";
        }

        // Remove undefined, null, or empty string fields
        Object.keys(transformed).forEach((key) => {
          if (
            transformed[key] === undefined ||
            transformed[key] === ""
          ) {
            delete transformed[key];
          }
        });

        return transformed;
      };

      const dataToSubmit = transformData(formData);
      onSubmit(dataToSubmit);
    }
  };

  const handleFieldChange = (field: keyof typeof formData, value: any) => {
    updateField(field, value);
  };

  if (!isLoaded) return <div>Loading...</div>;

  const renderFormContent = () => {
    switch (modalStep) {
      case 2:
        return (
          <div className={cx("job-address-container")}>
            <JobAddressInput
              value={formData.address}
              customerAddress={customerAddress}
              useCustomerAddress={useCustomerAddress}
              onChange={(value) => {
                handleFieldChange("address", value);
                if (useCustomerAddress) {
                  setUseCustomerAddress(false);
                }
              }}
              onSuggestionSelect={(suggestion) => {
                handleAddressSuggestionSelect(suggestion);
                if (useCustomerAddress) {
                  setUseCustomerAddress(false);
                }
              }}
              onUseCustomerAddressChange={(checked) => {
                setUseCustomerAddress(checked);
                if (checked && customerAddress) {
                  handleFieldChange("address", customerAddress);
                  handleFieldChange("latitude", 0);
                  handleFieldChange("longitude", 0);
                  setSelectedSuggestion(null);
                }
              }}
            />
          </div>
        );

      case 3:
        return (
          <div className={cx("job-details-container")}>
            <div className={cx("form-content")}>
              <div className={cx("job-details-form-content")}>
                <JobBasicInfo
                  formData={formData}
                  errors={errors}
                  salesReps={salesReps}
                  onFieldChange={handleFieldChange}
                  onErrorFieldChange={updateError}
                />

                <div className={cx("vertical-divider")}></div>

                <JobDetailsForm
                  formData={formData}
                  errors={errors}
                  tradeTypes={tradeTypes}
                  onFieldChange={handleFieldChange}
                  onErrorFieldChange={updateError}
                />
              </div>

              <JobTextAreas
                formData={formData}
                errors={errors}
                onFieldChange={handleFieldChange}
                onErrorFieldChange={updateError}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <BaseModal open={isOpen} onClose={onCancel} closeOnOverlay={false}>
      <div className={cx("add-job-container")}>
        <AddJobModalHeader currentStep={modalStep} onClose={onCancel} />
        <div className={cx("body")}>
          {renderFormContent()}
        </div>
        <AddJobModalActions
          currentStep={modalStep}
          jobStep="Existing"
          onBack={handleBackStep}
          onContinue={
            modalStep === 2
              ? handleConfirmAddress
              : handleCreateJob
          }
          disabled={modalStep === 3 ? !isDetailsStepValid() : false}
        />
      </div>
    </BaseModal>
  );
};

export default AddJobFromClientModal;